import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// Prime React Components
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';

// Needed URLS
import { GET_NEURELIS_PODCASTS, ADD_NEURELIS_PODCAST, EDIT_NEURELIS_PODCAST, DELETE_NEURELIS_PODCAST, GET_NEURELIS_AVAILABLE_PODCASTS, 
	NEURELIS_PDF_PATH } from '../constants/URLS';
const GET_ENTITY = GET_NEURELIS_PODCASTS
const ADD_ENTITY = ADD_NEURELIS_PODCAST
const EDIT_ENTITY = EDIT_NEURELIS_PODCAST
const DELETE_ENTITY = DELETE_NEURELIS_PODCAST

// Needed Components
import FileTypeChooser from '../components/fileTypeChooser'

const Podcasts = (props) => {

	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);
	const manuscriptUploadRef = useRef(null);
	const actionPlanUploadRef = useRef(null);
	const transcriptUploadRef = useRef(null);
	

	// CONSTANTS
    const emptyEntity = {
        podcastID: null,
        title: '',
		description: '',
		path: '',
		manuscript: '',
		manuscriptURL: '',
		manuscriptType: 'file',
		actionPlan: '',
		actionPlanURL: '',
		actionPlanType: 'file',
		transcript: '', 
		transcriptURL: '',
		transcriptType: 'file',
		isActive: 1
    };


	// STATE VARIABLES
    const [ entities, setEntities ] = useState([]); // holds all the existing 
	const [ entity, setEntity ] = useState(emptyEntity); // current entity that we are adding or editing
	const [ availablePodcasts, setAvailablePodcasts ] = useState([]); // current entity that we are adding or editing
	const [ entityDialog, setEntityDialog ] = useState(false);
    const [ deleteEntityDialog, setDeleteEntityDialog ] = useState(false);
    const [ submitted, setSubmitted ] = useState(false);
    const [ globalFilterValue1, setGlobalFilterValue1 ] = useState('');
	const [ filters, setFilters ] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
		
    });

	// USE EFFECTS HOOKS
    useEffect(() => {
		fncGet()
    }, []); 

	// ACTION / XHR FUNCTIONS
	const fncGet = async () => {
		
		let availablePodcastsXHR = await axios({method: 'get', url: GET_NEURELIS_AVAILABLE_PODCASTS})
		let _availablePodcasts = availablePodcastsXHR.data.results.map(cur => {
			return {
				label: cur, 
				value: cur
			}
		})
		setAvailablePodcasts(_availablePodcasts)
		
		let strURL = GET_ENTITY
		let entityXHR = await axios({method: 'get', url: strURL})
		// need to do some slight data modifications for the form

		if (entityXHR.data.success) {
			setEntities(entityXHR.data.results)
		} else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: entityXHR.data.message, life: 3000 });
		}
	};

	const fncSave = async () => {
        setSubmitted(true);
		let errors = []
		if (!entity.title.trim()) errors.push('title')
		

		if (errors.length > 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 3000 });
		}

		
		let _entity = {...entity};
		const formData = new FormData();
		formData.append("title", _entity.title);
		formData.append("path", _entity.path);
		formData.append("description", _entity.description);
		formData.append("manuscriptType", _entity.manuscriptType);
		formData.append("manuscript", _entity.manuscript);
		formData.append("manuscriptURL", _entity.manuscriptURL);
		formData.append("actionPlanType", _entity.actionPlanType);
		formData.append("actionPlan", _entity.actionPlan);
		formData.append("actionPlanURL", _entity.actionPlanURL);
		formData.append("transcriptType", _entity.transcriptType);
		formData.append("transcript", _entity.transcript);
		formData.append("transcriptURL", _entity.transcriptURL);
		
		
		formData.append("isActive", _entity.isActive);

		let manuscriptUploader = manuscriptUploadRef.current
		let arrManuFiles = manuscriptUploader.getFiles() || []
		if (arrManuFiles[0]) {
			formData.append("manuscriptFile", arrManuFiles[0]);
		}

		let actionPlanUploader = actionPlanUploadRef.current
		let arrActionFiles = actionPlanUploader.getFiles() || []
		if (arrActionFiles[0]) {
			formData.append("actionPlanFile", arrActionFiles[0]);
		}

		let transcriptUploader = transcriptUploadRef.current
		let arrTranFiles = transcriptUploader.getFiles() || []
		if (arrTranFiles[0]) {
			formData.append("transcriptFile", arrTranFiles[0]);
		}

		if (_entity.podcastID) {
			formData.append("podcastID", _entity.podcastID);
			let editResults = await axios({
				method: 'post', 
				url: EDIT_ENTITY,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (editResults.data.success) {
				await fncGet()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Podcast Edited', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		} else {
			
			let addResults = await axios({
				method: 'post', 
				url: ADD_ENTITY,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (addResults.data.success) {
				await fncGet()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Podcast Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
		}

		setSubmitted(false);
		setEntityDialog(false);
		clearEntity()
	}

	const fncDelete = async () => { 
		try {
			let deleteResults = await axios({
				method: 'post', 
				url: DELETE_ENTITY,
				data: {...entity}
			})
			if (deleteResults.data.success) {
				let _entities = entities.filter(val => val.podcastID !== entity.podcastID);
				setEntities(_entities);
				setDeleteEntityDialog(false);
				clearEntity()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Podcast Deleted', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
			}
		} catch (err) {
			toast.current.show({ severity: 'error', summary: 'Error', detail: 'There was an error deleting.', life: 3000 });
			console.error(err)
		}
	}
	
	
	// OTHER FUNCTIONS
	const clearEntity = () => {
		setEntity(emptyEntity); 
	}
	
	const openNew = () => {
		clearEntity()
		setSubmitted(false);
		setEntityDialog(true);
	}

	const hideDialog = () => {
        setSubmitted(false);
        setEntityDialog(false);
    }

	const onGlobalFilterChange1 = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue1(value);
    }

	const hideDeleteEntityDialog = () => {
        setDeleteEntityDialog(false);
    }

	const onInputChange = (value, name) => {
		let _entity = {...entity};
        _entity[`${name}`] = value;
        setEntity(_entity);
    }

	const fncEdit = (entity) => {
		setEntity(entity);
        setEntityDialog(true);
    }

	const confirmDeleteEntity = (entity) => {
        setEntity(entity);
        setDeleteEntityDialog(true);
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const actionBodyTemplate = (rowData) => {
        return (
            <>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => fncEdit(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteEntity(rowData)} />
            </>
        );
    }

	const dateTemplate = (rowData, column) => {
        return (
			<div className="flex flex-column">
				<div >
					{dayjs(rowData.timeStamp).format('MMM D, YYYY')}
				</div>
			</div>
		);
    }

	const detailsTemplate = (rowData) => {
		return (
			<div className="flex mslCard">
				<div className="title">
					{rowData.title}
				</div>
			</div>
		)
	}

	const optionalItemTemplate = (rowData, column) => {
		return (
			<div className="flex mslCard forceWrap">
				<div>
					{rowData[column.field + 'URL'] !== '' && 
						<div>
							{rowData[column.field + 'URL']}
						</div>
					}
					{rowData[column.field] !== '' && 
						<div>
							{rowData[column.field]}
						</div>
					}
				</div>
			</div>
		)
	}

	const activeTemplate = (rowData) => {
       if (rowData.isActive ===1 ) return <div className="green">Active</div>
	   else return <div className="red">Inactive</div>
    }

	

	// OTHER TEMPLATES (JSX)
	const leftToolbarTemplate = () => {
        return <Button label="Add New Podcast" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
    }

	const rightToolbarTemplate = () => {
        return null
    }
	
	const header = (
        <div className="table-header flex justify-content-between" >
            <h5 className="mx-0 my-1" >Manage Podcasts</h5>
			<div className="flex flex-column">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText value={globalFilterValue1} onChange={onGlobalFilterChange1} placeholder="Search..." />
				</span>
			</div>
        </div>
    );

	const entityDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={fncSave} />
        </>
    );

	

    const deleteEntityDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteEntityDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={fncDelete} />
        </>
    );


	const fileHeaderTemplate = (options) => {
        const { className, chooseButton } = options;
        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
            </div>
        );
    };
    

    return (
        <div className="datatable-resources">
             <Toast ref={toast} />

			<div className="card" style={{width:"100%"}}>
				<Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

				<DataTable ref={dt} value={entities} sortField="isActive" sortOrder={-1}  
					dataKey="podcastID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
					currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Podcasts"
					globalFilterFields={['title', 'transcript', 'actionPlan', 'manuscript']}
					filters={filters} filterDisplay="row" header={header} responsiveLayout="stack">
						<Column field="isActive" sortable body={activeTemplate} header="Active" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="title" header="Title" body={detailsTemplate} style={{ width: '24%', verticalAlign:'top' }}></Column>
						<Column field="path" header="Path" style={{ width: '14%', verticalAlign:'top' }}></Column>
						<Column field="actionPlan"  body={optionalItemTemplate} header="Action Plan" style={{ width: '14%', verticalAlign:'top' }}></Column>
						<Column field="manuscript"  body={optionalItemTemplate} header="Manuscript" style={{ width: '14%', verticalAlign:'top' }}></Column>
						<Column field="transcript"  body={optionalItemTemplate} header="Transcript" style={{ width: '14%', verticalAlign:'top' }}></Column>

						<Column body={actionBodyTemplate} exportable={false} style={{ width: '10%', verticalAlign:'top' }}></Column>
				</DataTable>
            </div>

			<Dialog visible={entityDialog} style={{ width: '850px' }} header="Podcast Details" modal className="p-fluid" footer={entityDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column inputForm">
					<div className="inputFormFields">
						<div className="flex">
							<div className="field fullField"  >
								<label htmlFor="title">Title</label>
								<InputText id="title" value={entity.title} onChange={(e) => onInputChange(e.target.value, 'title')} required className={classNames({ 'p-invalid': submitted && !entity.title })} />
								{submitted && !entity.title && <small className="p-error">Title is required.</small>}
							</div>
						</div>

						<div className="flex">
							<div className="field pr-4 twoThirdField" >
								<label htmlFor="description">Description</label>
								<InputText id="description" value={entity.description} onChange={(e) => onInputChange(e.target.value, 'description')} />
							</div>
							<div className="field oneThirdField">
								<label htmlFor="isActive">Active</label>
								<Dropdown value={entity.isActive} onChange={(e) => onInputChange(e.value,'isActive')} options={[{label: 'Active', value: 1},{label: 'Inactive',value: 0}]}  
									placeholder="Select a Status" />
							</div>
						</div>

						<div className="flex">
							<div className="field fullField">
								<label htmlFor="photo">MP3</label>
								<Dropdown value={entity.path} onChange={(e) => onInputChange(e.value,'path')} options={availablePodcasts}  
									placeholder="Select a Podcast"  />
							</div>
						</div>
						
						<div className="flex">
							<div className="field fullField">
								<label className="pr-2 " htmlFor="name">Manuscript (optional)</label>
								<div className="flex mb-2">
									
									<FileTypeChooser 
										onInputChange={onInputChange}
										entity={entity}
										fileUploadRef={manuscriptUploadRef}
										fileName="manuscriptFile"
										radioName="manuscriptType"
										inputName="manuscriptURL"
										currentName="manuscript"
										filePath={NEURELIS_PDF_PATH}
									/>
								</div>
							</div>
						</div>

						<div className="flex">
							<div className="field fullField">
								<label className="pr-2 " htmlFor="name">Action Plan  (optional)</label>
								<div className="flex mb-2">
									
									<FileTypeChooser 
										onInputChange={onInputChange}
										entity={entity}
										fileUploadRef={actionPlanUploadRef}
										fileName="actionPlanFile"
										radioName="actionPlanType"
										inputName="actionPlanURL"
										currentName="actionPlan"
										filePath={NEURELIS_PDF_PATH}
									/>
								</div>
							</div>
						</div>

						<div className="flex">
							<div className="field fullField">
								<label className="pr-2 " htmlFor="name">Transcript  (optional)</label>
								<div className="flex mb-2">
									
									<FileTypeChooser 
										onInputChange={onInputChange}
										entity={entity}
										fileUploadRef={transcriptUploadRef}
										fileName="transcriptFile"
										radioName="transcriptType"
										inputName="transcriptURL"
										currentName="transcript"
										filePath={NEURELIS_PDF_PATH}
									/>
								</div>
							</div>
						</div>

						
					</div>
				</div>
            </Dialog>
			<Dialog visible={deleteEntityDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteEntityDialogFooter} onHide={hideDeleteEntityDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {entity && <span>Are you sure you want to delete <b>{entity.title}</b>?</span>}
                </div>
            </Dialog>
        </div>
    );
}

export default Podcasts;